import queryString from 'query-string'
import {pickBy, isEqual, omit} from 'lodash'
import {Location} from 'history'
import moment from 'moment'
import {SearchAlertQuery, SortColumn, Order} from './query'
import {Schema} from './schema'

export interface SearchParams {
  q?: string
  alert_status?: 'all' | 'in_progress' | 'completed'
  alert_type_id?: string
  alert_start_start_on?: string
  alert_start_end_on?: string
  base_date_start_on?: string
  base_date_end_on?: string
  _page?: string
  limit?: string
  sort_type?: string
}

export const defaultSearchParams = {
  _page: '1',
  alert_status: 'in_progress',
  alert_start_start_on: moment().subtract(3, 'months').add(1, 'days').format('YYYY/MM/DD'),
  alert_start_end_on: moment().format('YYYY/MM/DD'),
  sort_type: 'alert_start__desc',
} satisfies SearchParams

export const getSearchParamsFromLocation = (location: Location): SearchParams => {
  return {
    ...defaultSearchParams,
    ...queryString.parse(location.search, {arrayFormat: 'bracket'}),
  }
}

export const convertSearchParamsToQueryString = (params: SearchParams): string =>
  queryString.stringify(params, {arrayFormat: 'bracket'})

export const convertSearchParamsToForm = (params: SearchParams): Schema => {
  return {
    searchValue: params.q || '',
    alertStartStartOn: params.alert_start_start_on || defaultSearchParams.alert_start_start_on,
    alertStartEndOn: params.alert_start_end_on || defaultSearchParams.alert_start_end_on,
    baseDateStartOn: params.base_date_start_on || '',
    baseDateEndOn: params.base_date_end_on || '',
    alertStatus: params.alert_status || defaultSearchParams.alert_status,
    alertTypeIds: params.alert_type_id?.split(',') || [],
    page: params._page || '1',
    limit: params.limit || '10',
    sortType: params.sort_type || defaultSearchParams.sort_type,
  }
}

export const convertFormToSearchParams = (form: Schema): SearchParams => {
  return pickBy({
    q: form.searchValue,
    alert_status: form.alertStatus,
    alert_type_id: form.alertTypeIds.join(','),
    alert_start_start_on: form.alertStartStartOn,
    alert_start_end_on: form.alertStartEndOn,
    base_date_start_on: form.baseDateStartOn,
    base_date_end_on: form.baseDateEndOn,
    _page: form.page,
    limit: undefined, // limitはSearchParamsに含めない
    sort_type: form.sortType,
  })
}

export const convertSearchParamsToGraphQL = (params: SearchParams): SearchAlertQuery => {
  const [sortColumn, sortOrder] = (params.sort_type || defaultSearchParams.sort_type).split('__')
  return {
    per: Number(params.limit) || 10,
    page: Number(params._page) || 1,
    search: {
      alertStartStartOn: params.alert_start_start_on || defaultSearchParams.alert_start_start_on,
      alertStartEndOn: params.alert_start_end_on || defaultSearchParams.alert_start_end_on,
      baseDateStartOn: params.base_date_start_on || undefined,
      baseDateEndOn: params.base_date_end_on || undefined,
      alertStatus:
        params.alert_status === 'in_progress' || params.alert_status === 'completed' ? params.alert_status : null,
      searchValue: params.q || '',
      alertTypeIds: params.alert_type_id ? params.alert_type_id.split(',').map(Number) : [],
      sortColumn: sortColumn as SortColumn,
      sortOrder: sortOrder as Order,
    },
  }
}

const ignoreFields: string[] = []

export const hasConditions = (form: Schema): boolean => {
  return !isEqual(
    pickBy(omit(convertFormToSearchParams(form), ignoreFields), (v) => (Array.isArray(v) ? v.length > 0 : v)),
    defaultSearchParams
  )
}
